// =========================================================================
// SightLine Networks — Production Website
// React + Tailwind CSS | Single-file implementation
//
// SEO: Each page has unique H1, meta title/description, canonical,
//      OG/Twitter tags, hreflang alternates, and JSON-LD schema managed by
//      SeoManager.
//
// i18n: The site ships in English (/) and Spanish (/es/...). Every URL is
//       defined once in src/i18n/routes.ts; all visible copy goes through
//       t("English source string") and is translated in src/i18n/es.ts.
//       Missing Spanish entries fall back to English rather than breaking.
//
// Google Ads: CTA buttons carry data-cta and data-page attributes.
//             trackCta() pushes to dataLayer (GTM) and gtag (GA4).
//             Form submissions fire a conversion-friendly event.
//
// Integration notes:
//   - Connect ContactForm to EmailJS / Supabase / your backend
//   - Add GTM container snippet in public/index.html <head>
//   - Add GA4 config snippet or load via GTM
//   - Add Google Ads conversion linker + conversion tags in GTM
// =========================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  Shield,
  Globe,
  Server,
  Monitor,
  Menu,
  X,
  CheckCircle2,
  MapPin,
  Headphones,
  Clock3,
  BadgeCheck,
  LineChart,
  Lock,
  Phone,
  Mail,
  Wifi,
  Camera,
  Cloud,
  ArrowRight,
  Check,
  Zap,
  HardDrive,
  Wrench,
  Building2,
  Languages,
} from "lucide-react";
import {
  BASE_URL,
  LANGS,
  LanguageProvider,
  SEO_CONFIG,
  htmlLangAttr,
  ogLocale,
  pathFor,
  resolveInitialLocation,
  resolveLocation,
  translate,
  urlFor,
  useLanguage,
  useT,
  writeLangPreference,
} from "./i18n";
import type { Lang, PageKey } from "./i18n";

// =========================================================================
// TYPES
// =========================================================================

// PageKey and Lang now live in src/i18n/types.ts because the routing table,
// the SEO config, and the sitemap generator all key off them.

type HeaderProps = {
  setPage: (page: PageKey) => void;
  page: PageKey;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
};

// =========================================================================
// CONSTANTS
// =========================================================================

const PHONE = "(727) 283-6452";
const PHONE_HREF = "tel:17272836452";
const EMAIL = "info@sightlinenetworks.com";

// Privacy notice banner — bump CONSENT_VERSION to re-show the banner to
// everyone after a material change to our data/cookie practices.
const CONSENT_VERSION = 1;
const CONSENT_COOKIE_NAME = "sln-privacy-consent";

// Consent is stored in a cookie scoped to the registrable parent domain so the
// acknowledgment is GLOBAL across every SightLine property (this marketing site
// and host.sightlinenetworks.com) rather than per-application. Keep this logic
// in sync with hosting-platform/src/utils/consent.js.
function consentDomainAttr(): string {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  // Share across *.sightlinenetworks.com in prod; host-only in dev/preview.
  return host === "sightlinenetworks.com" || host.endsWith(".sightlinenetworks.com")
    ? "; Domain=.sightlinenetworks.com"
    : "";
}

function hasAcknowledgedConsent(): boolean {
  if (typeof document === "undefined") return false;
  const match = document.cookie.match(/(?:^|;\s*)sln-privacy-consent=([^;]*)/);
  if (!match) return false;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return Boolean(parsed && parsed.acknowledged && parsed.version === CONSENT_VERSION);
  } catch {
    return false;
  }
}

function writeAcknowledgedConsent(): void {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(
    JSON.stringify({ acknowledged: true, version: CONSENT_VERSION })
  );
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${60 * 60 * 24 * 365}; Path=/` +
    `${consentDomainAttr()}; SameSite=Lax${secure}`;
}

const NAV_ITEMS: Array<{ key: PageKey; label: string }> = [
  { key: "services", label: "Services" },
  { key: "hosting", label: "Hosting" },
  { key: "solutions", label: "Solutions" },
  { key: "areas", label: "Areas Served" },
  { key: "about", label: "About" },
];

// Home page FAQ — visible content in HomePage MUST stay in sync with the
// FAQPage JSON-LD in public/index.html for Google rich-result eligibility.
const HOME_FAQS: Array<{ q: string; a: string }> = [
  {
    q: "Are you a certified Ubiquiti UniFi installer in Tampa Bay?",
    a: "Yes. SightLine Networks designs and installs Ubiquiti UniFi systems for Tampa Bay businesses, including UniFi Network (switches, access points, gateways), UniFi Protect security cameras, and UniFi Access door control. We handle planning, deployment, configuration, and ongoing support.",
  },
  {
    q: "Do you install Cisco networking and Cisco VOIP for businesses?",
    a: "Yes. We install Cisco and Cisco Meraki networking equipment — switches, routers, firewalls, and wireless — plus Cisco VOIP phone systems for offices that need enterprise reliability and call routing.",
  },
  {
    q: "Can you set up a Synology NAS for local backup?",
    a: "Absolutely. Synology NAS is one of our recommended storage and backup platforms. We size the unit, configure Synology DSM, set up RAID and snapshots, integrate with cloud backup providers, and document the recovery procedure for your team.",
  },
  {
    q: "Which IP camera brands do you install?",
    a: "We install Ubiquiti UniFi Protect, Hikvision, Axis Communications, Dahua, Amcrest, and Reolink. We pick the brand based on your site, budget, and integration needs (NVR vs. cloud, indoor vs. outdoor, license-plate or analytics requirements).",
  },
  {
    q: "Which VOIP phone systems do you support?",
    a: "We deploy and support business VOIP phones from Yealink, Poly (Polycom), and Grandstream, plus Cisco VOIP for enterprise environments. We can integrate with cloud PBX providers or set up a local PBX.",
  },
  {
    q: "Do you only work in Tampa Bay?",
    a: "We're based in Clearwater, FL and primarily serve the Tampa Bay area — Tampa, Clearwater, St. Petersburg, Largo, Pinellas Park, and Brandon. Larger projects outside the immediate area can be discussed on a case-by-case basis.",
  },
  {
    q: "How much does a UniFi or security camera installation cost?",
    a: "Cost depends on site size, number of access points or cameras, cabling required, and whether existing infrastructure can be reused. Most projects fall between a few thousand dollars for small offices and tens of thousands for multi-building deployments. We provide free, itemized quotes after a brief site discussion.",
  },
];

// =========================================================================
// SEO & TRACKING UTILITIES
// =========================================================================

function upsertMeta(
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string
) {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Publish the hreflang cluster for the current page.
 *
 * Both language versions of a page must point at each other (and at an
 * x-default) or Google treats them as unrelated near-duplicates instead of
 * alternates — which is what makes the Spanish tree rank for Spanish queries
 * without cannibalising the English one.
 */
function syncAlternateLinks(page: PageKey) {
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());

  const alternates: Array<[string, string]> = [
    ["en", urlFor(page, "en")],
    ["es", urlFor(page, "es")],
    // English is the fallback for any locale we don't explicitly target.
    ["x-default", urlFor(page, "en")],
  ];

  alternates.forEach(([hreflang, href]) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    document.head.appendChild(el);
  });
}

function buildSchema(page: PageKey, lang: Lang) {
  const seo = SEO_CONFIG[lang];
  const business = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${BASE_URL}/#business`,
    name: "SightLine Networks",
    url: BASE_URL,
    telephone: "+1-727-283-6452",
    email: EMAIL,
    areaServed: [
      "Tampa",
      "Largo",
      "Clearwater",
      "St. Petersburg",
      "Pinellas Park",
      "Brandon",
      "Tampa Bay",
    ],
    description: seo[page].description,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Clearwater",
      addressRegion: "FL",
      addressCountry: "US",
    },
    serviceType: [
      "Managed IT Services",
      "Network Installation",
      "Business Web Hosting",
      "Website Development",
      "Security Camera Systems",
      "Server and Cloud Support",
      "IT Consulting",
      "Ubiquiti UniFi Installation",
      "UniFi Protect Installation",
      "Cisco Network Installation",
      "Synology NAS Installation",
      "VOIP Phone Systems",
    ],
    brand: [
      "Ubiquiti", "UniFi", "UniFi Protect", "UniFi Network", "UniFi Access",
      "Cisco", "Cisco Meraki", "Synology",
      "Hikvision", "Axis Communications", "Dahua", "Amcrest", "Reolink",
      "Yealink", "Poly", "Grandstream",
    ].map((b) => ({ "@type": "Brand", name: b })),
  };

  const inLanguage = lang === "es" ? "es-US" : "en-US";
  const nodes: any[] = [{ ...business, inLanguage }];

  if (page === "home") {
    // The FAQ block rendered on the home page, in the active language.
    nodes.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage,
      mainEntity: HOME_FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: translate(q, lang),
        acceptedAnswer: { "@type": "Answer", text: translate(a, lang) },
      })),
    });
    return nodes;
  }

  if (page === "hosting") {
    nodes.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: translate("Business Web Hosting", lang),
      provider: { "@type": "ProfessionalService", "@id": `${BASE_URL}/#business` },
      areaServed: "Tampa Bay",
      inLanguage,
      description: seo.hosting.description,
      offers: [
        { "@type": "Offer", name: "Starter Hosting", price: "29", priceCurrency: "USD" },
        { "@type": "Offer", name: "Business Hosting", price: "59", priceCurrency: "USD" },
        { "@type": "Offer", name: "Pro Hosting", price: "99", priceCurrency: "USD" },
      ],
    });
    return nodes;
  }

  const servicePages: PageKey[] = ["managed-it", "network-installation", "security-cameras", "server-cloud", "website-development", "it-consulting"];
  if (servicePages.includes(page)) {
    const brandsByPage: Partial<Record<PageKey, string[]>> = {
      "network-installation": ["Ubiquiti UniFi", "Ubiquiti", "Cisco", "Cisco Meraki"],
      "security-cameras": ["Ubiquiti UniFi Protect", "Hikvision", "Axis Communications", "Dahua", "Amcrest", "Reolink"],
      "server-cloud": ["Synology", "Dell", "HPE"],
      "managed-it": ["Ubiquiti UniFi", "Cisco", "Synology", "Microsoft"],
    };
    const brandList = brandsByPage[page];
    nodes.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: seo[page].ogTitle.replace(" | SightLine Networks", ""),
      provider: { "@type": "ProfessionalService", "@id": `${BASE_URL}/#business` },
      areaServed: "Tampa Bay",
      inLanguage,
      description: seo[page].description,
      ...(brandList && {
        brand: brandList.map((b) => ({ "@type": "Brand", name: b })),
      }),
    });
    return nodes;
  }

  if (page === "areas") {
    return [{ ...business, "@type": "LocalBusiness", inLanguage }];
  }

  return nodes;
}

function SeoManager({ page, lang }: { page: PageKey; lang: Lang }) {
  useEffect(() => {
    const c = SEO_CONFIG[lang][page];
    const canonical = urlFor(page, lang);

    document.title = c.title;
    document.documentElement.lang = htmlLangAttr(lang);

    upsertMeta('meta[name="description"]', "name", "description", c.description);
    if (c.keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", c.keywords);
    }
    upsertMeta('meta[property="og:title"]', "property", "og:title", c.ogTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", c.ogDescription);
    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", ogLocale(lang));
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", c.ogTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", c.ogDescription);
    upsertLink("canonical", canonical);
    syncAlternateLinks(page);
    // Mirror the committed language out for trackCta(). Done in the effect
    // rather than during render so a discarded concurrent render can never
    // leave analytics reporting a language the user never saw.
    activeLang = lang;

    // public/index.html ships static English LocalBusiness + FAQPage JSON-LD,
    // which is only accurate for the English home page. On every other route
    // (including all Spanish routes) we strip those blocks and inject a graph
    // built for the actual page and language, so no page ever advertises
    // English FAQ content it does not render.
    const staticNodes = document.querySelectorAll(
      "#sln-static-business, #sln-static-faq"
    );
    const injected = document.getElementById("sln-schema") as HTMLScriptElement | null;

    if (page === "home" && lang === "en") {
      if (injected) injected.remove();
      return;
    }

    staticNodes.forEach((el) => el.remove());
    const tag = injected ?? document.createElement("script");
    if (!injected) {
      tag.type = "application/ld+json";
      tag.id = "sln-schema";
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(buildSchema(page, lang));
  }, [page, lang]);

  return null;
}

// The active language, mirrored out of React state so trackCta() can report a
// correct page_location without threading `lang` through ~40 CTA call sites.
let activeLang: Lang = "en";

/** Push CTA events to GTM dataLayer and GA4 gtag */
function trackCta(action: string, label: string, page: PageKey) {
  if (typeof window === "undefined") return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "cta_click",
    action,
    label,
    page,
    language: activeLang,
    timestamp: new Date().toISOString(),
  });
  if (typeof w.gtag === "function") {
    w.gtag("event", action, {
      event_category: "engagement",
      event_label: label,
      language: activeLang,
      page_location: urlFor(page, activeLang),
    });
  }
}

// =========================================================================
// SHARED UI COMPONENTS
// =========================================================================

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-600">
      {children}
    </p>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  text,
  onClick,
}: {
  icon: typeof Globe;
  title: string;
  text: string;
  onClick?: () => void;
}) {
  const t = useT();
  return (
    <article className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold tracking-tight text-slate-900">
        {title}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
        {text}
      </p>
      {onClick && (
        <button
          onClick={onClick}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
        >
          {t("Learn more")}{" "}
          <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </button>
      )}
    </article>
  );
}

function StatBlock({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  return (
    <div className="text-center">
      <div className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
        {value}
      </div>
      <div className="mt-1 text-sm text-slate-500">{label}</div>
    </div>
  );
}

function CtaButton({
  children,
  onClick,
  variant = "primary",
  ctaName,
  page,
  className = "",
  type = "button",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "white";
  ctaName?: string;
  page?: PageKey;
  className?: string;
  type?: "button" | "submit";
}) {
  const base = "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2";
  const variants = {
    primary: "bg-sky-600 text-white shadow-sm hover:bg-sky-700",
    secondary: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
    white: "bg-white text-sky-700 shadow-sm hover:bg-sky-50",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      data-cta={ctaName}
      data-page={page}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

// =========================================================================
// LANGUAGE SWITCHER
// =========================================================================

/**
 * EN/ES toggle. Rendered as real anchors pointing at the other language's URL
 * for this page — so the links are crawlable and open correctly in a new tab —
 * while a plain left-click is intercepted for client-side navigation.
 */
function LanguageSwitcher({
  page,
  className = "",
}: {
  page: PageKey;
  className?: string;
}) {
  const { lang, setLang, t } = useLanguage();

  return (
    <div
      role="group"
      aria-label={t("Switch language")}
      className={`flex items-center gap-0.5 rounded-lg border border-slate-200 p-0.5 ${className}`}
    >
      <Languages className="ml-1.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
      {LANGS.map((code) => {
        const active = code === lang;
        return (
          <a
            key={code}
            href={pathFor(page, code)}
            hrefLang={code}
            aria-current={active ? "true" : undefined}
            aria-label={
              code === "es"
                ? t("View this page in Spanish")
                : t("View this page in English")
            }
            onClick={(e) => {
              // Leave modified clicks (new tab / new window) to the browser.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              setLang(code);
            }}
            className={`rounded-md px-2 py-1 text-xs font-semibold uppercase transition ${
              active
                ? "bg-sky-600 text-white shadow-sm"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            {code}
          </a>
        );
      })}
    </div>
  );
}

// =========================================================================
// HEADER
// =========================================================================

function Header({ setPage, page, mobileOpen, setMobileOpen }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const t = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-white/95 backdrop-blur transition-shadow ${
        scrolled ? "border-slate-200 shadow-sm" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {/* Logo */}
        <button onClick={() => setPage("home")} aria-label={t("SightLine Networks home")} className="shrink-0">
          <img
            src={`${process.env.PUBLIC_URL}/sightline-logo-white.png`}
            alt="SightLine Networks"
            className="h-20 w-auto"
          />
        </button>

        {/* Desktop nav */}
        <nav aria-label={t("Primary navigation")} className="hidden items-center gap-1 lg:flex">
          <button
            onClick={() => setPage("home")}
            className={`rounded-lg px-3.5 py-2 text-sm font-medium transition ${
              page === "home"
                ? "bg-slate-100 text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            {t("Home")}
          </button>
          {NAV_ITEMS.map(({ key, label }) =>
            key === "hosting" ? (
              <a
                key={key}
                href="https://host.sightlinenetworks.com"
                className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
              >
                {t(label)}
              </a>
            ) : (
              <button
                key={key}
                onClick={() => setPage(key)}
                className={`rounded-lg px-3.5 py-2 text-sm font-medium transition ${
                  page === key
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {t(label)}
              </button>
            )
          )}
        </nav>

        {/* Desktop CTA cluster */}
        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher page={page} />
          <a
            href={PHONE_HREF}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 transition hover:text-slate-900"
            onClick={() => trackCta("phone_click", "header_phone", page)}
          >
            <Phone className="h-4 w-4" />
            {PHONE}
          </a>
          <CtaButton
            onClick={() => {
              trackCta("quote_click", "header_cta", page);
              setPage("contact");
            }}
            ctaName="header-quote"
            page={page}
          >
            {t("Get a Free Quote")}
          </CtaButton>
        </div>

        {/* Mobile: language switcher stays outside the menu so Spanish
            speakers can switch without opening the nav first. */}
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher page={page} />
          <button
            className="rounded-lg border border-slate-200 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? t("Close menu") : t("Open menu")}
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-6 py-4" aria-label={t("Mobile navigation")}>
            <button
              onClick={() => { setPage("home"); setMobileOpen(false); }}
              className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              {t("Home")}
            </button>
            {NAV_ITEMS.map(({ key, label }) =>
              key === "hosting" ? (
                <a
                  key={key}
                  href="https://host.sightlinenetworks.com"
                  className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  {t(label)}
                </a>
              ) : (
                <button
                  key={key}
                  onClick={() => { setPage(key); setMobileOpen(false); }}
                  className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  {t(label)}
                </button>
              )
            )}
            <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3">
              <a
                href={PHONE_HREF}
                className="flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", "mobile_cta", page);
                  setPage("contact");
                  setMobileOpen(false);
                }}
                ctaName="mobile-quote"
                page={page}
                className="w-full"
              >
                {t("Get a Free Quote")}
              </CtaButton>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

// =========================================================================
// FOOTER
// =========================================================================

function Footer({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  return (
    <footer className="border-t border-slate-800 bg-slate-950 text-slate-300">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <img
              src={`${process.env.PUBLIC_URL}/sightline-logo.png`}
              alt="SightLine Networks"
              className="h-20 w-auto"
            />
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              {t(
                "Managed IT services, business web hosting, network installation, security camera systems, and website development for companies across the Tampa Bay area."
              )}
            </p>
            <div className="mt-5 flex flex-col gap-2 text-sm">
              <a href={PHONE_HREF} className="flex items-center gap-2 text-slate-400 transition hover:text-white">
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 text-slate-400 transition hover:text-white">
                <Mail className="h-4 w-4" /> {EMAIL}
              </a>
            </div>
          </div>

          {/* Services links */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              {t("Services")}
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm" aria-label={t("Footer services")}>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("managed-it")}>{t("Managed IT Support")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("network-installation")}>{t("Network Installation")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("security-cameras")}>{t("Security Cameras")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("website-development")}>{t("Website Development")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("hosting")}>{t("Business Web Hosting")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("server-cloud")}>{t("Server & Cloud")}</button>
            </nav>
          </div>

          {/* Company links */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              {t("Company")}
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm" aria-label={t("Footer company")}>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("about")}>{t("About Us")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("solutions")}>{t("Solutions")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("areas")}>{t("Areas Served")}</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("contact")}>{t("Contact")}</button>
            </nav>
          </div>

          {/* Areas served */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              {t("Areas Served")}
            </h2>
            <div className="mt-4 flex flex-col gap-2.5 text-sm text-slate-400">
              <span>Tampa</span>
              <span>Clearwater</span>
              <span>Largo</span>
              <span>St. Petersburg</span>
              <span>Pinellas Park</span>
              <span>Brandon</span>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 md:flex-row">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} SightLine Networks.{" "}
            {t("All rights reserved. Clearwater, FL — Serving Tampa Bay and beyond.")}
          </p>
          <div className="flex gap-4 text-xs text-slate-500">
            <button className="transition hover:text-slate-300" onClick={() => setPage("privacy")}>{t("Privacy Policy")}</button>
            <button
              className="transition hover:text-slate-300"
              onClick={() => {
                setPage("privacy");
                if (typeof window !== "undefined") {
                  window.setTimeout(() => {
                    const el = document.getElementById("do-not-sell");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 120);
                }
              }}
            >
              {t("Do Not Sell or Share My Personal Information")}
            </button>
            <button className="transition hover:text-slate-300" onClick={() => setPage("contact")}>{t("Terms of Service")}</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// =========================================================================
// HOME PAGE
// =========================================================================

function HomePage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-sky-50/60 via-white to-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(14,165,233,0.08),transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-16 lg:pb-28 lg:pt-24">
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-sky-700">
                <MapPin className="h-3.5 w-3.5" />
                {t("Serving Tampa Bay businesses")}
              </div>

              <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                {t("IT services and managed hosting built for")}{" "}
                <span className="text-sky-600">{t("growing businesses")}</span>
              </h1>

              <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
                {t(
                  "From network installation and security cameras to business web hosting and website development, SightLine Networks gives your company the technology backbone it needs to operate without interruption."
                )}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <CtaButton
                  onClick={() => {
                    trackCta("quote_click", "hero_primary", "home");
                    setPage("contact");
                  }}
                  ctaName="hero-quote"
                  page="home"
                >
                  {t("Get a Free Quote")} <ArrowRight className="h-4 w-4" />
                </CtaButton>
                <CtaButton
                  variant="secondary"
                  onClick={() => {
                    trackCta("services_click", "hero_secondary", "home");
                    setPage("services");
                  }}
                  ctaName="hero-services"
                  page="home"
                >
                  {t("Explore Our Services")}
                </CtaButton>
              </div>

              {/* Trust bar */}
              <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-slate-200 pt-6 sm:gap-10">
                <StatBlock value="500+" label={t("Projects completed")} />
                <StatBlock value="24/7" label={t("Monitoring & support")} />
                <StatBlock value="99.9%" label={t("Hosting uptime")} />
              </div>
            </div>

            {/* Solution finder widget */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50">
              <div className="flex items-center justify-between pb-4">
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    {t("What does your business need?")}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {t("Pick one to get started")}
                  </div>
                </div>
                <div className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  {t("Quick start")}
                </div>
              </div>

              <div className="space-y-2.5">
                {[
                  { label: "Better Wi-Fi and network performance", target: "network-installation" as PageKey, icon: Wifi },
                  { label: "Security cameras for the business", target: "security-cameras" as PageKey, icon: Camera },
                  { label: "A professional website or redesign", target: "website-development" as PageKey, icon: Monitor },
                  { label: "Reliable web hosting with support", target: "hosting" as PageKey, icon: Cloud },
                  { label: "Ongoing managed IT support", target: "managed-it" as PageKey, icon: Headphones },
                  { label: "Server or cloud infrastructure", target: "server-cloud" as PageKey, icon: Server },
                ].map(({ label, target, icon: ItemIcon }) => (
                  <button
                    key={label}
                    onClick={() => {
                      trackCta("solution_selector", label, "home");
                      setPage(target);
                    }}
                    className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  >
                    <ItemIcon className="h-4 w-4 shrink-0 text-sky-600" />
                    <span className="flex-1">{t(label)}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core services */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <SectionLabel>{t("What we do")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Technology services that keep your business running")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "We handle IT infrastructure, networking, security, hosting, and web development so you can focus on running your business."
            )}
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={Globe}
            title={t("Network Installation")}
            text={t("Structured cabling, switches, access points, and enterprise-grade Wi-Fi designed for your facility and growth plans.")}
            onClick={() => setPage("network-installation")}
          />
          <FeatureCard
            icon={Shield}
            title={t("Security Camera Systems")}
            text={t("Professional surveillance design and installation with remote viewing, cloud storage, and HD video across your property.")}
            onClick={() => setPage("security-cameras")}
          />
          <FeatureCard
            icon={Server}
            title={t("Server & Cloud")}
            text={t("On-premise server installation, cloud migration, backup solutions, and hybrid infrastructure for reliable operations.")}
            onClick={() => setPage("server-cloud")}
          />
          <FeatureCard
            icon={Monitor}
            title={t("Website Development")}
            text={t("Responsive business websites with modern design, SEO foundations, lead generation, and ongoing content management.")}
            onClick={() => setPage("website-development")}
          />
          <FeatureCard
            icon={Lock}
            title={t("Business Web Hosting")}
            text={t("Managed hosting with SSL, daily backups, performance monitoring, and support — so your website is always online.")}
            onClick={() => setPage("hosting")}
          />
          <FeatureCard
            icon={Headphones}
            title={t("Managed IT Support")}
            text={t("Proactive monitoring, helpdesk support, software updates, and technology guidance from a local Tampa Bay team.")}
            onClick={() => setPage("managed-it")}
          />
        </div>
      </section>

      {/* Why choose us */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <SectionLabel>{t("Why SightLine Networks")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("A technology partner you can count on")}
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: MapPin, title: "Local Tampa Bay team", text: "On-site support and in-person consultations across the Tampa Bay area. We know the local business landscape." },
              { icon: Clock3, title: "Fast response times", text: "Same-day support for urgent issues. We monitor systems 24/7 and resolve problems before they impact your operations." },
              { icon: BadgeCheck, title: "Certified professionals", text: "Our engineers hold certifications from Cisco, Microsoft, VMware, and other leading technology vendors." },
              { icon: LineChart, title: "Transparent pricing", text: "No hidden fees or surprise charges. Clear proposals, predictable monthly costs, and flexible service agreements." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-slate-900">{t(title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment brands */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-3xl">
            <SectionLabel>{t("Ubiquiti UniFi, Cisco & Synology specialists")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Certified installers of UniFi Protect, UniFi Network, Cisco & Synology in Tampa Bay")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "SightLine Networks designs, installs, and supports business systems built on Ubiquiti UniFi (UniFi Network, UniFi Protect, and UniFi Access), Cisco networking and VOIP equipment, and Synology NAS for local backup and storage. We also install IP security cameras from Hikvision, Axis, and Dahua, plus VOIP phone systems from Yealink, Poly, and Grandstream."
              )}
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { name: "Ubiquiti UniFi", category: "UniFi Network, UniFi Protect & UniFi Access — cameras, networking & security", logoUrl: `${process.env.PUBLIC_URL}/brands/unifi.png` },
              { name: "Cisco", category: "Cisco & Meraki networking, switches and VOIP" },
              { name: "Synology", category: "Synology NAS — local storage, backup & DSM" },
              { name: "Hikvision", category: "IP security cameras & NVR systems", logoUrl: `${process.env.PUBLIC_URL}/brands/hikvision.png` },
              { name: "Axis Communications", category: "Enterprise IP cameras", logoUrl: `${process.env.PUBLIC_URL}/brands/axis.png` },
              { name: "Dahua", category: "IP cameras & surveillance", logoUrl: `${process.env.PUBLIC_URL}/brands/dahua.png` },
              { name: "Yealink", category: "VOIP desk phones & conference phones" },
              { name: "Poly (Polycom)", category: "VOIP & video conferencing" },
              { name: "Grandstream", category: "VOIP phones, gateways & PBX" },
            ].map(({ name, category, logoUrl }) => (
              <div
                key={name}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-sky-300 hover:shadow-md"
              >
                <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl bg-slate-50 px-3">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`${name} logo`}
                      className="h-10 w-auto max-w-[80px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-base font-bold tracking-tight text-slate-700">
                      {name}
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-base font-semibold text-slate-900">{name}</div>
                  <div className="mt-0.5 text-sm text-slate-600">{t(category)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — mirrors FAQPage JSON-LD in public/index.html */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>{t("Frequently asked questions")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Common questions about our brands & services")}
            </h2>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {HOME_FAQS.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-900 marker:hidden">
                  <span>{t(q)}</span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t("Ready to improve your business technology?")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sky-100">
            {t(
              "Tell us about your project and get a free, no-obligation quote from our Tampa Bay team. Most quotes returned within one business day."
            )}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", "home_bottom_cta", "home");
                setPage("contact");
              }}
              ctaName="home-bottom-quote"
              page="home"
            >
              {t("Get a Free Quote")} <ArrowRight className="h-4 w-4" />
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
              onClick={() => trackCta("phone_click", "home_bottom_phone", "home")}
            >
              <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// SERVICES PAGE
// =========================================================================

function ServicesPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  // Copy is stored in English and translated at render time — the English
  // string doubles as the dictionary key (see src/i18n/es.ts).
  const services: Array<{ icon: typeof Headphones; title: string; text: string; features: string[]; page: PageKey }> = [
    {
      icon: Headphones,
      title: "Managed IT Support",
      text: "Proactive monitoring, helpdesk support, patch management, and strategic technology guidance. We keep your systems running so your team stays productive.",
      features: ["24/7 monitoring", "Remote & on-site support", "Vendor management", "Technology roadmaps"],
      page: "managed-it",
    },
    {
      icon: Globe,
      title: "Network Installation",
      text: "Professional network design and deployment including structured cabling, switches, firewalls, access points, and enterprise-grade Wi-Fi coverage.",
      features: ["Structured cabling", "Wireless design", "Firewall setup", "Network audits"],
      page: "network-installation",
    },
    {
      icon: Camera,
      title: "Security Camera Systems",
      text: "Commercial surveillance system design, installation, and support. HD and 4K cameras with remote viewing, cloud storage, and motion-triggered alerts.",
      features: ["HD/4K cameras", "Remote viewing", "Cloud recording", "Access control"],
      page: "security-cameras",
    },
    {
      icon: Server,
      title: "Server & Cloud Infrastructure",
      text: "On-premise server installation, cloud migration, hybrid environments, and disaster recovery planning for businesses that depend on data availability.",
      features: ["Server setup", "Cloud migration", "Backup & recovery", "Virtualization"],
      page: "server-cloud",
    },
    {
      icon: Monitor,
      title: "Website Development",
      text: "Modern, responsive business websites designed for performance, search visibility, and lead generation. Built to work across all devices with ongoing support available.",
      features: ["Responsive design", "SEO foundations", "Lead capture", "CMS management"],
      page: "website-development",
    },
    {
      icon: Lock,
      title: "Business Web Hosting",
      text: "Managed hosting plans built for company websites with free SSL, daily automated backups, uptime monitoring, and expert support when you need it.",
      features: ["Free SSL", "Daily backups", "99.9% uptime", "Managed support"],
      page: "hosting",
    },
    {
      icon: LineChart,
      title: "IT Consulting & Strategy",
      text: "Technology assessments, infrastructure planning, compliance guidance, and strategic IT roadmaps for businesses preparing for growth or change.",
      features: ["IT assessments", "Compliance guidance", "Vendor evaluation", "Budget planning"],
      page: "it-consulting",
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Our services")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Business IT services for Tampa Bay companies")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "From managed IT support and network installation to security cameras, hosting, and website development — we provide the technology services your business needs to run smoothly and grow confidently."
              )}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", "services_hero", "services");
                  setPage("contact");
                }}
                ctaName="services-quote"
                page="services"
              >
                {t("Request a Quote")}
              </CtaButton>
              <CtaButton
                variant="secondary"
                onClick={() => setPage("hosting")}
              >
                {t("View Hosting Plans")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {services.map((svc) => (
            <article
              key={svc.title}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <svc.icon className="h-5 w-5" />
              </div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                {t(svc.title)}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t(svc.text)}
              </p>
              <ul className="mt-4 grid grid-cols-2 gap-2">
                {svc.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-1.5 text-xs text-slate-500"
                  >
                    <Check className="h-3.5 w-3.5 text-sky-500" /> {t(f)}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setPage(svc.page)}
                data-cta="service-detail"
                data-service={svc.title}
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
              >
                {svc.title === "Business Web Hosting"
                  ? t("View plans")
                  : t("Learn more")}{" "}
                <ChevronRight className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* Cross-link CTA */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("Not sure which service you need?")}
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                {t(
                  "Our solutions page helps you find the right service based on the business problem you are trying to solve. Or talk to our team directly for a personalized recommendation."
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <CtaButton onClick={() => setPage("solutions")}>
                {t("Browse Solutions")}
              </CtaButton>
              <CtaButton
                variant="secondary"
                onClick={() => {
                  trackCta("quote_click", "services_bottom", "services");
                  setPage("contact");
                }}
                ctaName="services-bottom-quote"
                page="services"
              >
                {t("Talk to Our Team")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// HOSTING PAGE
// =========================================================================

function HostingPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const [billing, setBilling] = useState<"monthly" | "annual">("annual");

  const plans = [
    {
      name: "Starter",
      monthlyPrice: 29,
      annualPrice: 290,
      annualMonthly: 24,
      description: "For small business websites, landing pages, and brochure sites that need reliable uptime and basic support.",
      features: [
        "1 website",
        "10 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Uptime monitoring",
        "Email support",
        "99.9% uptime SLA",
      ],
      featured: false,
    },
    {
      name: "Business",
      monthlyPrice: 59,
      annualPrice: 590,
      annualMonthly: 49,
      description: "For growing business websites that need faster performance, priority support, and room to scale.",
      features: [
        "Up to 3 websites",
        "25 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Uptime & performance monitoring",
        "Priority phone & email support",
        "Staging environment",
        "99.9% uptime SLA",
      ],
      featured: true,
    },
    {
      name: "Pro",
      monthlyPrice: 99,
      annualPrice: 990,
      annualMonthly: 83,
      description: "For high-traffic websites, custom applications, and businesses that need maximum performance and support.",
      features: [
        "Up to 10 websites",
        "50 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Advanced performance monitoring",
        "Dedicated support manager",
        "Staging environment",
        "Disaster recovery",
        "99.99% uptime SLA",
      ],
      featured: false,
    },
  ];

  const faqItems = [
    {
      q: "What types of websites can I host?",
      a: "Our plans support WordPress sites, custom-built business websites, landing pages, e-commerce stores, and web applications. We optimize for the platform you use.",
    },
    {
      q: "Is SSL included with all plans?",
      a: "Yes. Every hosting plan includes a free SSL certificate that auto-renews, ensuring your site is always served over HTTPS for visitor trust and SEO.",
    },
    {
      q: "How do backups work?",
      a: "We run daily automated backups of your website files and databases. Business and Pro plans include one-click restore. Backups are stored off-server for safety.",
    },
    {
      q: "Can I bundle hosting with website development?",
      a: "Absolutely. Many of our clients pair a website build with ongoing hosting and managed support. We offer package pricing when you combine services.",
    },
    {
      q: "What happens if my site goes down?",
      a: "Our monitoring systems detect downtime within minutes and our team begins remediation immediately. Business and Pro plans include proactive alerting and faster response.",
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center lg:py-20">
          <SectionLabel>{t("Business web hosting")}</SectionLabel>
          <h1 className="mx-auto mt-3 max-w-3xl text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            {t("Fast, secure hosting with managed support for your business website")}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
            {t(
              "Every plan includes free SSL, daily backups, uptime monitoring, and hands-on support from our Tampa Bay team. Pick a plan and launch with confidence."
            )}
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => setBilling("monthly")}
              className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                billing === "monthly"
                  ? "bg-sky-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("Monthly")}
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                billing === "annual"
                  ? "bg-sky-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("Annual")}{" "}
              <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                billing === "annual" ? "bg-sky-500 text-white" : "bg-emerald-50 text-emerald-700"
              }`}>
                {t("Save 17%")}
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="mx-auto max-w-7xl px-6 -mt-2 pb-20">
        <div className="grid gap-6 lg:grid-cols-3" aria-label={t("View Hosting Plans")}>
          {plans.map((plan) => {
            const price = billing === "annual" ? plan.annualMonthly : plan.monthlyPrice;
            const total = billing === "annual" ? plan.annualPrice : null;
            return (
              <article
                key={plan.name}
                className={`relative flex flex-col rounded-2xl border p-7 shadow-sm transition hover:shadow-md ${
                  plan.featured
                    ? "border-sky-300 bg-white shadow-sky-100/50 ring-1 ring-sky-200"
                    : "border-slate-200 bg-white"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-6 rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                    {t("Most Popular")}
                  </div>
                )}
                <h2 className="text-xl font-bold text-slate-900">{plan.name}</h2>
                <p className="mt-2 text-sm text-slate-500">{t(plan.description)}</p>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight text-slate-900">
                    ${price}
                  </span>
                  <span className="text-sm text-slate-500">{t("/mo")}</span>
                </div>
                {total && (
                  <p className="mt-1 text-xs text-slate-400">
                    ${total}/{t("year")} {t("billed annually")}
                  </p>
                )}

                <CtaButton
                  onClick={() => {
                    trackCta("hosting_plan_click", plan.name, "hosting");
                    setPage("contact");
                  }}
                  ctaName={`hosting-${plan.name.toLowerCase()}`}
                  page="hosting"
                  className={`mt-6 w-full ${plan.featured ? "" : ""}`}
                  variant={plan.featured ? "primary" : "secondary"}
                >
                  {t("Choose")} {plan.name}
                </CtaButton>

                <ul className="mt-6 flex-1 space-y-3 border-t border-slate-100 pt-6">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-slate-600"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                      {t(f)}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {/* Guarantee */}
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-slate-500">
          <Shield className="h-5 w-5 text-sky-600" />
          <span>{t("30-day money-back guarantee on all plans. No long-term contracts required.")}</span>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">
              {t("Everything included with your hosting")}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              {t("Enterprise-grade infrastructure and hands-on management for every plan.")}
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Lock, title: "Free SSL Certificate", text: "Auto-renewing HTTPS encryption for every website on your plan." },
              { icon: HardDrive, title: "Daily Backups", text: "Automated daily backups stored off-server with easy restore options." },
              { icon: Zap, title: "Performance Optimized", text: "SSD storage, server-level caching, and CDN-ready infrastructure." },
              { icon: Headphones, title: "Expert Support", text: "Real people who understand your hosting, available when you need them." },
              { icon: Shield, title: "Security Monitoring", text: "Firewall protection, malware scanning, and proactive threat detection." },
              { icon: Globe, title: "99.9% Uptime SLA", text: "Guaranteed availability backed by infrastructure redundancy." },
              { icon: Wrench, title: "Managed Updates", text: "We handle CMS, plugin, and server updates so your site stays current." },
              { icon: ArrowRight, title: "Free Migration", text: "We move your existing website to our platform at no extra cost." },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <Icon className="mb-3 h-5 w-5 text-sky-600" />
                <h3 className="text-sm font-semibold text-slate-900">{t(title)}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{t(text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[0.4fr_0.6fr]">
          <div>
            <SectionLabel>{t("Hosting FAQ")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
              {t("Common questions about our hosting")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("Can't find what you're looking for? Our team is happy to answer any hosting questions.")}
            </p>
            <CtaButton
              onClick={() => {
                trackCta("quote_click", "hosting_faq_cta", "hosting");
                setPage("contact");
              }}
              className="mt-6"
              ctaName="hosting-faq-contact"
              page="hosting"
            >
              {t("Ask a Question")}
            </CtaButton>
          </div>

          <div className="space-y-6">
            {faqItems.map(({ q, a }) => (
              <div key={q} className="border-b border-slate-200 pb-6 last:border-0">
                <h3 className="text-base font-semibold text-slate-900">{t(q)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-14 text-center">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            {t("Ready to host your business website with confidence?")}
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-100">
            {t(
              "Choose a plan above or talk to our team about custom hosting needs. We also bundle hosting with website development."
            )}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", "hosting_bottom", "hosting");
                setPage("contact");
              }}
              ctaName="hosting-bottom-quote"
              page="hosting"
            >
              {t("Talk to Our Team")}
            </CtaButton>
            <CtaButton
              variant="secondary"
              onClick={() => setPage("services")}
              className="border-sky-500 bg-transparent text-white hover:bg-sky-700"
            >
              {t("View All Services")}
            </CtaButton>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// REUSABLE SERVICE PAGE TEMPLATE
// =========================================================================

function ServicePageTemplate({
  setPage,
  icon: Icon,
  title,
  serviceName,
  subtitle,
  intro,
  features,
  benefits,
  relatedServices,
  faq,
  brands,
}: {
  setPage: (page: PageKey) => void;
  icon: typeof Globe;
  /** Long, keyword-rich H1. English source string; translated at render. */
  title: string;
  /**
   * Short lowercase noun phrase for mid-sentence use ("network installation").
   * Kept separate from `title` because the H1 is too long to read well inside
   * a sentence, and because Spanish needs the article baked into the phrase
   * ("la instalación de redes") for these headings to be grammatical.
   */
  serviceName: string;
  subtitle: string;
  intro: string;
  features: Array<{ title: string; text: string }>;
  benefits: string[];
  relatedServices: Array<{ label: string; page: PageKey }>;
  faq: Array<{ q: string; a: string }>;
  brands?: Array<{ name: string; logoUrl?: string }>;
}) {
  const t = useT();
  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
              <Icon className="h-6 w-6" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t(title)}
            </h1>
            <p className="mt-2 text-lg font-medium text-sky-600">{t(subtitle)}</p>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">{t(intro)}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", `${title}_hero`, "services");
                  setPage("contact");
                }}
                ctaName={`service-${title}-quote`}
                page="services"
              >
                {t("Get a Free Quote")} <ArrowRight className="h-4 w-4" />
              </CtaButton>
              <CtaButton variant="secondary" onClick={() => setPage("services")}>
                {t("View All Services")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <SectionLabel>{t("What's included")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            {t("How we deliver")} {t(serviceName)}
          </h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">{t(f.title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(f.text)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Brands */}
      {brands && brands.length > 0 && (
        <section className="border-b border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-7xl px-6 py-14">
            <div className="mb-8 text-center">
              <SectionLabel>{t("Brands we work with")}</SectionLabel>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("Trusted brands we install and support")}
              </h2>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {brands.map((brand) => (
                <div
                  key={brand.name}
                  className="flex h-20 w-36 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 shadow-sm transition hover:shadow-md"
                >
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={`${brand.name} logo`}
                      className="h-10 w-auto max-w-[120px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-center text-sm font-bold tracking-tight text-slate-700">
                      {brand.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("Why businesses choose SightLine for")} {t(serviceName)}
              </h2>
              <ul className="mt-6 space-y-3">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
                    <span>{t(b)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">{t("Related services")}</h3>
              <p className="mt-2 text-sm text-slate-500">{t("Explore other services that pair well.")}</p>
              <div className="mt-4 flex flex-col gap-2">
                {relatedServices.map((rs) => (
                  <button
                    key={rs.label}
                    onClick={() => setPage(rs.page)}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  >
                    {t(rs.label)}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20">
          <div className="grid gap-12 lg:grid-cols-[0.4fr_0.6fr]">
            <div>
              <SectionLabel>{t("FAQ")}</SectionLabel>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                {t("Common questions")}
              </h2>
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", `${title}_faq`, "services");
                  setPage("contact");
                }}
                className="mt-6"
              >
                {t("Ask a Question")}
              </CtaButton>
            </div>
            <div className="space-y-6">
              {faq.map(({ q, a }) => (
                <div key={q} className="border-b border-slate-200 pb-6 last:border-0">
                  <h3 className="text-base font-semibold text-slate-900">{t(q)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(a)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-14 text-center">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            {t("Ready to get started with")} {t(serviceName)}?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-100">
            {t("Tell us about your project and get a free quote from our Tampa Bay team.")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", `${title}_bottom`, "services");
                setPage("contact");
              }}
            >
              {t("Get a Free Quote")}
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
            >
              <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// INDIVIDUAL SERVICE PAGES
// =========================================================================

function ManagedItPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Headphones}
      title="Managed IT Support"
      serviceName="managed IT support"
      subtitle="Proactive IT management for Tampa Bay businesses"
      intro="Stop putting out IT fires. Our managed IT service provides 24/7 monitoring, helpdesk support, cybersecurity, patch management, and strategic technology guidance — so your team stays productive and your systems stay secure."
      features={[
        { title: "24/7 Monitoring & Alerting", text: "We monitor your servers, network, and endpoints around the clock and respond to issues before they affect your operations." },
        { title: "Helpdesk Support", text: "Your team gets fast access to experienced technicians for troubleshooting, how-to questions, and technical guidance via phone, email, or remote session." },
        { title: "Patch Management", text: "We keep your operating systems, applications, and firmware up to date with tested patches that reduce vulnerabilities without disrupting work." },
        { title: "Cybersecurity", text: "Endpoint protection, email filtering, DNS security, and firewall management to defend your business against ransomware, phishing, and data breaches." },
        { title: "Vendor Management", text: "We coordinate with your ISP, software vendors, and hardware suppliers so you have a single point of contact for all technology issues." },
        { title: "Technology Roadmaps", text: "Quarterly business reviews with strategic recommendations on upgrades, budgets, and IT investments aligned to your business goals." },
      ]}
      benefits={[
        "Reduce downtime with proactive monitoring and faster issue resolution",
        "Predictable monthly costs instead of expensive break-fix surprises",
        "Local Tampa Bay team available for on-site support when needed",
        "Certified engineers with Cisco, Microsoft, and VMware expertise",
        "Single point of contact for all your technology needs",
        "Scalable support that grows with your business",
      ]}
      relatedServices={[
        { label: "Network Installation", page: "network-installation" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Security Cameras", page: "security-cameras" },
        { label: "IT Consulting & Strategy", page: "it-consulting" },
      ]}
      faq={[
        { q: "What does managed IT include?", a: "Our managed IT service covers 24/7 monitoring, helpdesk support, patch management, cybersecurity, vendor coordination, and strategic technology planning. We tailor the service to your business needs." },
        { q: "How fast is your response time?", a: "Critical issues receive immediate attention. Standard support requests are typically addressed within one hour during business hours, with 24/7 emergency coverage available." },
        { q: "Do you offer on-site support?", a: "Yes. While most issues are resolved remotely, we provide on-site support throughout the Tampa Bay area whenever hands-on work is needed." },
        { q: "Can you take over from our current IT provider?", a: "Absolutely. We handle the full onboarding process including documentation, asset inventory, credential transfer, and a transition plan to minimize disruption." },
      ]}
    />
  );
}

function NetworkInstallationPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Globe}
      title="Ubiquiti UniFi & Cisco Network Installation in Tampa Bay"
      serviceName="network installation"
      subtitle="Certified UniFi and Cisco installers for business networks"
      intro="Whether you're building out a new office, upgrading aging infrastructure, or expanding to a new location, we design and install Ubiquiti UniFi and Cisco networks that are fast, reliable, and built to grow with your business — including UniFi access points, Cisco/Meraki switches, firewalls, and structured cabling."
      features={[
        { title: "Structured Cabling", text: "Cat6/Cat6A cabling runs, cable management, patch panels, and rack installations built to commercial standards with full documentation." },
        { title: "Enterprise Wi-Fi Design", text: "Site surveys, heat mapping, and access point deployment for seamless wireless coverage across your entire facility without dead zones." },
        { title: "Switches & Firewalls", text: "Managed switches, VLANs, QoS configuration, and next-gen firewall deployment for secure, segmented network infrastructure." },
        { title: "Network Audits", text: "Comprehensive assessment of your existing infrastructure to identify bottlenecks, security gaps, and upgrade opportunities." },
        { title: "VPN & Remote Access", text: "Secure site-to-site and remote access VPN configuration so your team can work from anywhere without compromising security." },
        { title: "Documentation & Training", text: "Complete network diagrams, port maps, credential documentation, and training for your team on day-to-day management." },
      ]}
      benefits={[
        "Professionally designed and installed by certified network engineers",
        "Clean, documented cabling that passes inspection and lasts for years",
        "Enterprise-grade Wi-Fi with complete coverage and zero dead zones",
        "Scalable design that accommodates business growth without rework",
        "Full documentation and network diagrams provided at handoff",
        "Ongoing support available through our managed IT service",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Security Cameras", page: "security-cameras" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Business Web Hosting", page: "hosting" },
      ]}
      faq={[
        { q: "Can you install cabling in an existing building?", a: "Yes. We install structured cabling in new and existing spaces, including retrofit projects. We work around your schedule to minimize disruption to daily operations." },
        { q: "What brands do you work with?", a: "We deploy equipment from Cisco, Ubiquiti (UniFi), Aruba, Fortinet, and other enterprise-grade vendors. We recommend the best fit based on your needs and budget." },
        { q: "How long does a typical installation take?", a: "Small office installations typically take 1-3 days. Larger projects with structured cabling and multiple floors may take 1-2 weeks. We provide a detailed timeline in your proposal." },
        { q: "Do you offer network maintenance after installation?", a: "Yes. We can provide ongoing managed IT support that includes network monitoring, firmware updates, and troubleshooting as part of our managed IT service." },
      ]}
    />
  );
}

function SecurityCamerasPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Camera}
      title="UniFi Protect, Hikvision &amp; Axis Security Camera Installation in Tampa Bay"
      serviceName="security camera installation"
      subtitle="Certified IP camera installers for Tampa Bay businesses"
      intro="Protect your property, employees, and assets with professional-grade IP security camera systems from Ubiquiti UniFi Protect, Hikvision, Axis Communications, Dahua, Amcrest, and Reolink. We handle camera layout design, installation, NVR or cloud recording, remote viewing, and integration with access control."
      brands={[
        { name: "UniFi Protect", logoUrl: `${process.env.PUBLIC_URL}/brands/unifi.png` },
        { name: "Amcrest", logoUrl: `${process.env.PUBLIC_URL}/brands/amcrest.png` },
        { name: "Hikvision", logoUrl: `${process.env.PUBLIC_URL}/brands/hikvision.png` },
        { name: "Reolink", logoUrl: `${process.env.PUBLIC_URL}/brands/reolink.png` },
        { name: "Axis", logoUrl: `${process.env.PUBLIC_URL}/brands/axis.png` },
        { name: "Dahua", logoUrl: `${process.env.PUBLIC_URL}/brands/dahua.png` },
      ]}
      features={[
        { title: "Camera System Design", text: "We survey your property, identify coverage needs, and design a camera layout that eliminates blind spots and covers all critical areas." },
        { title: "HD & 4K IP Cameras", text: "Commercial-grade IP cameras with high-resolution video, night vision, wide-angle lenses, and weather-resistant housings for indoor and outdoor use." },
        { title: "Remote Viewing", text: "View live and recorded footage from any device — phone, tablet, or computer — from anywhere with an internet connection." },
        { title: "Cloud & Local Recording", text: "Flexible storage options including on-site NVR recording, cloud storage, or hybrid solutions based on your retention requirements." },
        { title: "Motion Detection & Alerts", text: "Intelligent motion-triggered recording with real-time push notifications so you know immediately when something needs attention." },
        { title: "Access Control Integration", text: "Pair cameras with door access systems, intercoms, and gate controls for a unified physical security solution." },
      ]}
      benefits={[
        "Deter theft, vandalism, and unauthorized access to your business",
        "Crystal clear HD and 4K footage for identification and evidence",
        "View your cameras from anywhere on your phone or computer",
        "Professional installation with clean cable runs and concealed wiring",
        "Scalable systems that expand easily as your needs grow",
        "Local Tampa Bay team for installation, training, and ongoing support",
      ]}
      relatedServices={[
        { label: "Network Installation", page: "network-installation" },
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "IT Consulting", page: "it-consulting" },
      ]}
      faq={[
        { q: "How many cameras do I need?", a: "It depends on your property size and coverage requirements. We provide a free site survey to determine the optimal number and placement of cameras for your business." },
        { q: "Can I view cameras on my phone?", a: "Yes. All of our camera systems include mobile app access so you can view live and recorded footage from your smartphone or tablet at any time." },
        { q: "How long is footage stored?", a: "Storage duration depends on your setup. Local NVR systems typically store 30-90 days of footage. Cloud storage plans offer flexible retention periods. We help you choose the right option." },
        { q: "Do you service existing camera systems?", a: "Yes. We can assess, repair, upgrade, or expand existing camera systems, regardless of the original installer. We also offer maintenance plans." },
      ]}
    />
  );
}

function ServerCloudPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Server}
      title="Synology NAS, Server &amp; Cloud Infrastructure in Tampa Bay"
      serviceName="server and cloud infrastructure"
      subtitle="Synology, on-premise servers, and cloud migration"
      intro="From Synology NAS installation and on-premise servers to cloud migration and hybrid environments, we build infrastructure that keeps your data available, your applications running, and your business protected against downtime and data loss — including local backup, virtualization, and disaster recovery."
      features={[
        { title: "Server Installation", text: "Physical server deployment including rack mounting, OS installation, Active Directory configuration, and integration with your existing network." },
        { title: "Cloud Migration", text: "Plan and execute migrations to AWS, Azure, Google Cloud, or Microsoft 365 with minimal downtime and full data verification." },
        { title: "Hybrid Environments", text: "Design infrastructure that balances on-premise and cloud resources for the best combination of performance, cost, and data control." },
        { title: "Backup & Disaster Recovery", text: "Automated backup solutions with offsite replication, tested recovery procedures, and documented RPO/RTO targets for your business." },
        { title: "Virtualization", text: "VMware and Hyper-V virtualization to consolidate hardware, improve resource utilization, and simplify server management." },
        { title: "Storage Solutions", text: "Network-attached storage (NAS), SAN configuration, and shared file systems designed for performance, redundancy, and easy collaboration." },
      ]}
      benefits={[
        "Infrastructure designed for your specific workloads and growth plans",
        "Tested backup and recovery procedures that protect your business data",
        "Cloud solutions that reduce hardware costs while improving reliability",
        "Virtualization that maximizes your existing hardware investment",
        "Ongoing monitoring and management through our managed IT service",
        "Local Tampa Bay engineers available for on-site server work",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Network Installation", page: "network-installation" },
        { label: "Business Web Hosting", page: "hosting" },
        { label: "IT Consulting", page: "it-consulting" },
      ]}
      faq={[
        { q: "Should I use on-premise servers or the cloud?", a: "It depends on your workloads, compliance requirements, and budget. Many businesses benefit from a hybrid approach. We assess your situation and recommend the best fit." },
        { q: "Can you migrate our data to the cloud?", a: "Yes. We plan and execute cloud migrations to platforms like Microsoft 365, Azure, AWS, and Google Cloud with full data verification and minimal disruption." },
        { q: "How do you handle backups?", a: "We implement automated backup solutions with local and offsite copies, scheduled verification, and documented recovery procedures tailored to your business needs." },
        { q: "Do you provide ongoing server management?", a: "Yes. Server monitoring, patching, and management are included in our managed IT service. We proactively maintain your infrastructure to prevent issues." },
      ]}
    />
  );
}

function WebsiteDevelopmentPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Monitor}
      title="Website Development"
      serviceName="website development"
      subtitle="Professional business websites for Tampa Bay companies"
      intro="Your website is often the first impression customers have of your business. We build modern, mobile-friendly websites designed to look professional, load fast, rank in search engines, and convert visitors into leads and customers."
      features={[
        { title: "Responsive Design", text: "Websites that look and work perfectly on every device — desktop, tablet, and mobile — with modern layouts and clean typography." },
        { title: "SEO Foundations", text: "Semantic HTML, meta tags, structured data, page speed optimization, and content structure that helps your site rank in Google and other search engines." },
        { title: "Lead Generation", text: "Contact forms, quote request CTAs, click-to-call buttons, and conversion tracking designed to turn website visitors into business inquiries." },
        { title: "CMS Management", text: "Content management systems that let you update text, images, and pages without needing a developer for everyday changes." },
        { title: "Performance Optimization", text: "Fast load times through image optimization, code splitting, caching strategies, and CDN delivery that improve both user experience and SEO." },
        { title: "Ongoing Support", text: "Post-launch support for updates, content changes, security patches, and feature additions — so your site stays current and secure." },
      ]}
      benefits={[
        "Professional design that builds trust with visitors and customers",
        "Mobile-first approach ensures great experience on all devices",
        "SEO foundations that help you rank for local and industry searches",
        "Lead generation features that convert traffic into business inquiries",
        "Pair with managed hosting for a complete website solution",
        "Local Tampa Bay team for ongoing support and collaboration",
      ]}
      relatedServices={[
        { label: "Business Web Hosting", page: "hosting" },
        { label: "Managed IT Support", page: "managed-it" },
        { label: "IT Consulting & Strategy", page: "it-consulting" },
        { label: "Network Installation", page: "network-installation" },
      ]}
      faq={[
        { q: "How long does it take to build a website?", a: "A typical business website takes 4-8 weeks from kickoff to launch. Timeline depends on scope, content readiness, and revision rounds. We provide a detailed schedule in your proposal." },
        { q: "Can you redesign my existing website?", a: "Yes. We handle full redesigns, migrations from other platforms, and incremental improvements to existing sites. We assess your current site and recommend the best approach." },
        { q: "Do you provide hosting for the websites you build?", a: "Yes. We offer managed hosting plans with SSL, daily backups, monitoring, and support — designed specifically for business websites. See our hosting page for plan details." },
        { q: "Will my website be optimized for search engines?", a: "Yes. Every website we build includes SEO foundations: semantic HTML, meta tags, structured data, fast load times, and mobile-friendly design. We also offer ongoing SEO services." },
      ]}
    />
  );
}

function ItConsultingPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={LineChart}
      title="IT Consulting & Strategy"
      serviceName="IT consulting"
      subtitle="Technology guidance for Tampa Bay businesses"
      intro="Make smarter technology decisions with strategic IT consulting. We provide assessments, infrastructure planning, compliance guidance, vendor evaluation, and IT roadmaps that align your technology investments with your business goals."
      features={[
        { title: "Technology Assessments", text: "Comprehensive evaluation of your current infrastructure, security posture, and technology stack with prioritized recommendations for improvement." },
        { title: "Infrastructure Planning", text: "Design technology architectures for new offices, expansions, relocations, or major upgrades — with clear specifications and budget estimates." },
        { title: "Compliance Guidance", text: "Navigate HIPAA, PCI-DSS, SOC 2, and industry-specific compliance requirements with documentation and controls tailored to your business." },
        { title: "Vendor Evaluation", text: "Objective assessment and comparison of technology vendors, platforms, and solutions so you make informed purchasing decisions." },
        { title: "Budget Planning", text: "IT budget development including capital vs. operational expense analysis, lifecycle planning, and ROI projections for proposed investments." },
        { title: "IT Roadmaps", text: "Multi-year technology plans that align IT investments with business growth, risk reduction, and operational improvement goals." },
      ]}
      benefits={[
        "Objective technology advice not tied to specific vendor relationships",
        "Clear, actionable recommendations with priority and budget context",
        "Compliance guidance that prepares your business for audits and certifications",
        "Strategic planning that prevents expensive reactive technology purchases",
        "Experience across industries including healthcare, professional services, and retail",
        "Ongoing advisory available as part of our managed IT service",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Network Installation", page: "network-installation" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Website Development", page: "website-development" },
      ]}
      faq={[
        { q: "When should I hire an IT consultant?", a: "Common triggers include office moves, rapid growth, compliance requirements, aging infrastructure, security concerns, or the need for a technology roadmap. If you're unsure, a free initial consultation can help." },
        { q: "Do you only consult, or do you implement too?", a: "Both. We provide strategic consulting as a standalone service, and we can also implement the solutions we recommend through our other service lines." },
        { q: "How is consulting priced?", a: "Consulting can be project-based (fixed scope and fee), hourly, or included as part of a managed IT agreement. We provide clear pricing in every proposal." },
        { q: "Can you help with compliance requirements?", a: "Yes. We help businesses understand and meet HIPAA, PCI-DSS, SOC 2, and other regulatory and industry compliance requirements through assessments, documentation, and controls implementation." },
      ]}
    />
  );
}

// =========================================================================
// SOLUTIONS PAGE
// =========================================================================

function SolutionsPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const solutions = [
    {
      icon: Building2,
      title: "Setting up a new office",
      text: "Moving into a new space? We handle network cabling, Wi-Fi setup, phone systems, printers, security cameras, and workstation deployment so your team is productive from day one.",
      services: ["Network Installation", "Security Cameras", "Managed IT"],
      cta: "network-installation" as PageKey,
    },
    {
      icon: Wifi,
      title: "Fixing slow or unreliable Wi-Fi",
      text: "Dead zones, dropped connections, and slow speeds cost your team hours every week. We audit your wireless environment and deploy enterprise-grade access points for reliable coverage.",
      services: ["Network Installation", "Managed IT"],
      cta: "network-installation" as PageKey,
    },
    {
      icon: Camera,
      title: "Adding security cameras",
      text: "Protect your business with professional-grade surveillance. We design camera layouts, install equipment, set up remote viewing, and configure cloud or local recording.",
      services: ["Security Cameras", "Network Installation"],
      cta: "security-cameras" as PageKey,
    },
    {
      icon: Monitor,
      title: "Building a professional website",
      text: "Your website is often the first impression customers have of your business. We build modern, mobile-friendly sites designed to convert visitors into leads and customers.",
      services: ["Website Development", "Web Hosting", "SEO"],
      cta: "website-development" as PageKey,
    },
    {
      icon: Headphones,
      title: "Getting ongoing IT support",
      text: "Stop putting out fires. Our managed IT service provides proactive monitoring, helpdesk support, security updates, and technology planning so issues are handled before they impact your work.",
      services: ["Managed IT", "Server & Cloud"],
      cta: "managed-it" as PageKey,
    },
    {
      icon: Cloud,
      title: "Choosing the right web hosting",
      text: "Not all hosting is equal. We offer managed hosting plans with SSL, daily backups, monitoring, and real support — designed for business websites that need to stay online.",
      services: ["Web Hosting", "Website Development"],
      cta: "hosting" as PageKey,
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Solutions")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Technology solutions for common business challenges")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "Start with the problem you are trying to solve. We will match you with the right service, explain what is involved, and give you a clear path to getting it done."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Solution cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {solutions.map((sol) => (
            <article
              key={sol.title}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <sol.icon className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {t(sol.title)}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {t(sol.text)}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {sol.services.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"
                  >
                    {t(s)}
                  </span>
                ))}
              </div>
              <button
                onClick={() => setPage(sol.cta)}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
              >
                {t("See how we help")} <ChevronRight className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-sky-50">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                {t("Need help choosing the right solution?")}
              </h2>
              <p className="mt-2 text-slate-600">
                {t("Our team will assess your situation and recommend the best path forward — no obligation.")}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", "solutions_cta", "solutions");
                  setPage("contact");
                }}
                ctaName="solutions-quote"
                page="solutions"
              >
                {t("Get a Free Quote")}
              </CtaButton>
              <CtaButton variant="secondary" onClick={() => setPage("hosting")}>
                {t("View Hosting Plans")}
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// AREAS SERVED PAGE
// =========================================================================

function AreasPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const areas = [
    {
      name: "Tampa",
      desc: "Managed IT, network installation, security cameras, web hosting, and website development for businesses throughout Tampa and Hillsborough County.",
    },
    {
      name: "Clearwater",
      desc: "Our home base. Full-service IT support, networking, surveillance, and web solutions for Clearwater businesses and surrounding areas.",
    },
    {
      name: "Largo",
      desc: "On-site and remote IT services, hosting, and technology support for businesses in Largo and mid-Pinellas County.",
    },
    {
      name: "St. Petersburg",
      desc: "Comprehensive business technology services including managed IT, web development, camera systems, and hosting for St. Pete companies.",
    },
    {
      name: "Pinellas Park",
      desc: "Network installation, IT support, security cameras, and business hosting services for Pinellas Park offices and commercial spaces.",
    },
    {
      name: "Brandon",
      desc: "Managed IT, networking, cloud infrastructure, and web services for businesses in the Brandon and eastern Hillsborough area.",
    },
  ];

  const serviceCategories = [
    "Managed IT support",
    "Network installation & Wi-Fi",
    "Security camera systems",
    "Business web hosting",
    "Website development",
    "Server & cloud infrastructure",
    "IT consulting & strategy",
    "Ongoing technology support",
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Service coverage")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("IT services across the Tampa Bay area")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "SightLine Networks provides managed IT, web hosting, networking, security cameras, and website development to businesses throughout Tampa Bay. We offer both on-site and remote support."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Area cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((area) => (
            <article
              key={area.name}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-sky-600" />
                <h2 className="text-lg font-bold text-slate-900">
                  {area.name}
                </h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {t(area.desc)}
              </p>
              <button
                onClick={() => {
                  trackCta("area_quote", area.name, "areas");
                  setPage("contact");
                }}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
              >
                {t("Get support in")} {area.name}{" "}
                <ChevronRight className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* Service categories */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            {t("Services available across Tampa Bay")}
          </h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            {t("Every service we offer is available to businesses throughout our coverage area, with on-site support where needed.")}
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {serviceCategories.map((svc) => (
              <div
                key={svc}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0 text-sky-500" />
                {t(svc)}
              </div>
            ))}
          </div>
          <CtaButton
            onClick={() => {
              trackCta("quote_click", "areas_bottom", "areas");
              setPage("contact");
            }}
            className="mt-8"
            ctaName="areas-quote"
            page="areas"
          >
            {t("Request Local Support")}
          </CtaButton>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// ABOUT PAGE
// =========================================================================

function AboutPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("About us")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Your Tampa Bay technology partner")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "SightLine Networks was founded to give Tampa Bay businesses access to the same caliber of IT services, hosting, and technology infrastructure that large enterprises rely on — without the complexity or inflated costs."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Story */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Built for business. Based in Tampa Bay.")}
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>
                {t(
                  "We started SightLine Networks because we saw too many local businesses struggling with unreliable IT vendors, confusing pricing, and technology that was holding them back instead of moving them forward."
                )}
              </p>
              <p>
                {t(
                  "Today, we serve companies across Tampa, Clearwater, Largo, St. Petersburg, and beyond with managed IT, network installation, security cameras, web hosting, website development, and ongoing technology support."
                )}
              </p>
              <p>
                {t(
                  "Our approach is simple: understand what your business actually needs, deliver it professionally, and be available when you need us. No jargon, no hidden fees, no excuses."
                )}
              </p>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {[
              { value: "500+", label: "Projects completed", icon: CheckCircle2 },
              { value: "24/7", label: "System monitoring", icon: Clock3 },
              { value: "99.9%", label: "Hosting uptime", icon: Server },
              { value: "Same-day", label: "Urgent response", icon: Zap },
            ].map(({ value, label, icon: Icon }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center shadow-sm"
              >
                <Icon className="mb-3 h-6 w-6 text-sky-600" />
                <div className="text-2xl font-bold text-slate-900">{t(value)}</div>
                <div className="mt-1 text-sm text-slate-500">{t(label)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("What guides how we work")}
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: "Clarity over complexity", text: "We explain technology in plain language, give clear proposals, and make sure you understand what you are paying for and why." },
              { title: "Proactive, not reactive", text: "We monitor systems, apply updates, and identify risks before they become problems. Prevention is always cheaper than recovery." },
              { title: "Local presence, real support", text: "We are based in the Tampa Bay area. When you need on-site help, we show up. When you call, a real person answers." },
              { title: "Right-sized solutions", text: "We recommend what your business actually needs — not the most expensive option. Solutions should match your size and goals." },
              { title: "Long-term relationships", text: "We build technology partnerships, not transactional vendor relationships. Our best clients have been with us for years." },
              { title: "Transparent pricing", text: "No hidden fees, no surprise invoices, no bait-and-switch. You will know exactly what services cost before we start." },
            ].map(({ title, text }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="text-base font-semibold text-slate-900">{t(title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="rounded-2xl bg-sky-600 px-8 py-12 text-center shadow-lg shadow-sky-200/30">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            {t("Let's talk about what your business needs")}
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-100">
            {t("Whether you need managed IT, a new website, hosting, or a full technology overhaul — we're happy to discuss it.")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", "about_cta", "about");
                setPage("contact");
              }}
              ctaName="about-quote"
              page="about"
            >
              {t("Get a Free Quote")}
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
            >
              <Phone className="h-4 w-4" /> {t("Call")} {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// CONTACT PAGE
// =========================================================================

function ContactPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    trackCta("form_submit", "contact_form", "contact");

    try {
      const res = await fetch(
        "https://jadtcuydqkdezwmvbdih.supabase.co/functions/v1/send-contact-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImphZHRjdXlkcWtkZXp3bXZiZGloIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc3MTEwMjksImV4cCI6MjA4MzI4NzAyOX0.dhMk9TkTaKCv1DGKKf0f4IO3ZYNbXI2UwG3zg10FA28",
          },
          body: JSON.stringify({
            name: form.name,
            company: form.company || "",
            email: form.email,
            phone: form.phone || "",
            subject: form.service || "General Inquiry",
            message: form.message || "No additional message provided",
            source: "main-website",
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to send email");
      }

      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus("error");
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>{t("Get in touch")}</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {t("Let's discuss your project")}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              {t(
                "Tell us what your business needs and we will get back to you with a clear recommendation and a free, no-obligation quote — usually within one business day."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Form */}
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">
              {t("Request a free quote")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t("Fill out the form and our team will respond within one business day.")}
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
              id="contact-form"
              data-form="contact"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Full Name *")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("Your name")}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Email *")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("you@company.com")}
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Phone")}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("(555) 000-0000")}
                  />
                </div>
                <div>
                  <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t("Company")}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder={t("Your company")}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t("What do you need help with? *")}
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={form.service}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="">{t("Select a service")}</option>
                  <option value="managed-it">{t("Managed IT Support")}</option>
                  <option value="network">{t("Network Installation")}</option>
                  <option value="cameras">{t("Security Cameras")}</option>
                  <option value="server">{t("Server & Cloud")}</option>
                  <option value="website">{t("Website Development")}</option>
                  <option value="hosting">{t("Web Hosting")}</option>
                  <option value="consulting">{t("IT Consulting")}</option>
                  <option value="other">{t("Other / Not Sure")}</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t("Tell us about your project")}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                  placeholder={t("Describe your needs, timeline, or any questions you have...")}
                />
              </div>

              <CtaButton
                type="submit"
                ctaName="contact-submit"
                page="contact"
                className="w-full sm:w-auto"
              >
                {status === "sending"
                  ? t("Sending...")
                  : status === "success"
                  ? t("Quote Requested!")
                  : t("Submit Quote Request")}
              </CtaButton>

              {status === "success" && (
                <p className="text-sm font-medium text-emerald-600">
                  {t("Thank you! We received your request and will be in touch within one business day.")}
                </p>
              )}
              {status === "error" && (
                <p className="text-sm font-medium text-red-600">
                  {t("Something went wrong. Please call us at")} {PHONE}{" "}
                  {t("or email")} {EMAIL}.
                </p>
              )}
            </form>
          </div>

          {/* Sidebar */}
          <div className="flex flex-col gap-6">
            {/* Direct contact */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                {t("Prefer to talk directly?")}
              </h3>
              <div className="mt-4 space-y-4">
                <a
                  href={PHONE_HREF}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  onClick={() => trackCta("phone_click", "contact_sidebar", "contact")}
                >
                  <Phone className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">{PHONE}</div>
                    <div className="text-xs text-slate-500">{t("Call or text")}</div>
                  </div>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  onClick={() => trackCta("email_click", "contact_sidebar", "contact")}
                >
                  <Mail className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">{EMAIL}</div>
                    <div className="text-xs text-slate-500">{t("Email us anytime")}</div>
                  </div>
                </a>
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                  <MapPin className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">Clearwater, FL</div>
                    <div className="text-xs text-slate-500">
                      {t("Serving the greater Tampa Bay area")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust signals */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="text-lg font-semibold text-slate-900">
                {t("Why businesses choose SightLine")}
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  "Free, no-obligation quotes",
                  "Response within one business day",
                  "Local Tampa Bay team",
                  "Transparent pricing — no hidden fees",
                  "500+ projects completed",
                  "Certified engineers (Cisco, Microsoft, VMware)",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm text-slate-600"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-sky-500" />
                    {t(item)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Service links */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                {t("Explore our services")}
              </h3>
              <div className="mt-4 flex flex-col gap-2">
                {[
                  { label: "View all services", page: "services" as PageKey },
                  { label: "Hosting plans & pricing", page: "hosting" as PageKey },
                  { label: "Solutions by need", page: "solutions" as PageKey },
                  { label: "Areas we serve", page: "areas" as PageKey },
                ].map(({ label, page: p }) => (
                  <button
                    key={label}
                    onClick={() => setPage(p)}
                    className="flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    {t(label)}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// PRIVACY NOTICE BANNER (CCPA/CPRA notice-only)
// =========================================================================

function PrivacyNoticeBanner({ setPage }: { setPage: (page: PageKey) => void }) {
  const { lang, t } = useLanguage();
  // Prerender/SSR safe: start hidden so the banner is never baked into the
  // static HTML produced by react-snap. We only reveal it after reading
  // localStorage on the client.
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    // Don't surface the banner during the react-snap prerender snapshot, so it
    // is never baked into the static HTML (which would flash for returning,
    // already-consented visitors until hydration hides it again).
    if (typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent)) {
      return;
    }
    // Consent is read from a cookie shared across all SightLine subdomains, so
    // an acknowledgment made on the hosting platform counts here too — global
    // consent, not per-application.
    if (!hasAcknowledgedConsent()) setVisible(true);
  }, []);

  // Slide/fade the card in on the frame after it mounts (the motion-reduce:
  // classes below make it appear instantly for reduced-motion users).
  useEffect(() => {
    if (!visible) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [visible]);

  const dismiss = () => {
    writeAcknowledgedConsent();
    setVisible(false);
  };

  const goToPrivacy = (anchor?: string) => {
    setPage("privacy");
    if (anchor && typeof window !== "undefined") {
      // Wait for the privacy page to mount (and the page-change scroll-to-top
      // effect to run) before scrolling to the requested section.
      window.setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  };

  if (!visible) return null;

  const linkCls =
    "rounded text-sky-600 underline-offset-2 transition hover:text-sky-700 hover:underline focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2";

  return (
    <div
      role="region"
      aria-label={t("Privacy notice")}
      className={`fixed z-50 inset-x-4 bottom-4 sm:inset-x-auto sm:left-6 sm:bottom-6 sm:w-[26rem] transition-all duration-500 ease-out motion-reduce:transition-none ${
        entered
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100"
      }`}
    >
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10">
        {/* Brand accent strip */}
        <div className="h-1 w-full bg-gradient-to-r from-sky-500 to-cyan-400" />

        <div className="p-5 sm:p-6">
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("Dismiss privacy notice")}
            className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-3 pr-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600 ring-1 ring-sky-100">
              <Shield className="h-5 w-5" />
            </span>
            <h2 className="font-['Space_Grotesk'] text-base font-bold tracking-tight text-slate-900">
              {t("Your privacy, your choice")}
            </h2>
          </div>

          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            {t(
              "We use cookies for analytics (Google Analytics) and advertising (Google Ads), which may share identifiers like your IP and cookie IDs with those providers. By using this site you consent — California residents can opt out anytime."
            )}
          </p>

          <div className="mt-3 flex flex-col items-start gap-1.5 text-[13px] font-medium">
            <a
              href={urlFor("privacy", lang)}
              onClick={(e) => {
                e.preventDefault();
                goToPrivacy();
              }}
              className={linkCls}
            >
              {t("Privacy Policy")}
            </a>
            <a
              href={`${urlFor("privacy", lang)}#do-not-sell`}
              onClick={(e) => {
                e.preventDefault();
                goToPrivacy("do-not-sell");
              }}
              className={linkCls}
            >
              {t("Do Not Sell or Share My Personal Information")}
            </a>
          </div>

          <div className="mt-5">
            <CtaButton
              variant="primary"
              ctaName="privacy-consent-ack"
              onClick={dismiss}
              className="w-full"
            >
              {t("Got it")}
            </CtaButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// PRIVACY POLICY PAGE
// =========================================================================

function PrivacyPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const { lang, t } = useLanguage();
  const h2 = "mt-10 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl";
  const p = "mt-4 text-sm leading-relaxed text-slate-600";
  const ul = "mt-4 space-y-2 text-sm leading-relaxed text-slate-600";
  const li = "flex gap-2";
  const linkCls =
    "rounded text-sky-600 underline-offset-2 transition hover:text-sky-700 hover:underline focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2";

  const Bullet = ({ children }: { children: React.ReactNode }) => (
    <li className={li}>
      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
      <span>{children}</span>
    </li>
  );

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:py-20">
          <SectionLabel>{t("Legal")}</SectionLabel>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            {t("Privacy Policy")}
          </h1>
          <p className="mt-4 text-sm font-medium text-slate-500">
            {t("Last Updated: June 27, 2026")}
          </p>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            {t(
              'SightLine Networks ("we," "us," or "our") respects your privacy. This Privacy Policy explains what personal information we collect, how we use and share it, and the rights you have — including rights specific to California residents under the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA).'
            )}
          </p>

          {/* The English text remains the controlling version of this policy —
              say so explicitly on the translated page rather than implying the
              Spanish text is independently binding. */}
          {lang !== "en" && (
            <p className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-600">
              {t(
                "This Spanish translation is provided for convenience. In case of any discrepancy, the English version of this Privacy Policy governs."
              )}{" "}
              <a href={urlFor("privacy", "en")} hrefLang="en" className={linkCls}>
                {t("View this page in English")}
              </a>
            </p>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-4xl px-6 py-16 lg:py-20">
        {/* 1 */}
        <h2 className={h2}>{t("1. Information We Collect")}</h2>
        <p className={p}>{t("We collect the following categories of personal information:")}</p>
        <ul className={ul}>
          <Bullet>
            {t("Identifiers: name, email address, phone number, IP address, and online identifiers (such as cookie IDs) when you contact us, request a quote, create an account, or browse the site.")}
          </Bullet>
          <Bullet>
            {t("Commercial information: services or plans you purchase or inquire about, and billing details (payment card data is processed by our payment provider and is not stored on our servers).")}
          </Bullet>
          <Bullet>
            {t("Internet or other electronic network activity: pages viewed, links clicked, referring URLs, and how you interact with our site, collected through cookies and analytics tools.")}
          </Bullet>
          <Bullet>
            {t("Geolocation data: approximate location derived from your IP address.")}
          </Bullet>
          <Bullet>
            {t("Inferences drawn from the above to understand your interest in our services.")}
          </Bullet>
        </ul>
        <p className={p}>
          <span className="font-semibold text-slate-900">
            {t("Sources of this information.")}
          </span>{" "}
          {t("We collect personal information directly from you (for example, when you contact us, request a quote, or create an account), automatically from your device and browser as you use our site (through cookies and similar technologies), and from service providers such as our analytics and advertising partners.")}
        </p>
        <p className={p}>
          {t("We do not knowingly collect sensitive personal information beyond what you voluntarily provide to us.")}
        </p>

        {/* 2 */}
        <h2 className={h2}>{t("2. How We Use Your Information")}</h2>
        <ul className={ul}>
          <Bullet>{t("To provide, operate, and maintain our website and services;")}</Bullet>
          <Bullet>{t("To respond to your inquiries, quotes, and support requests;")}</Bullet>
          <Bullet>{t("To process transactions and manage accounts;")}</Bullet>
          <Bullet>{t("To measure and improve site performance and marketing;")}</Bullet>
          <Bullet>{t("To deliver and measure advertising;")}</Bullet>
          <Bullet>
            {t("To comply with legal obligations and protect against fraud and abuse.")}
          </Bullet>
        </ul>

        {/* 3 */}
        <h2 className={h2}>{t("3. Cookies and Tracking Technologies")}</h2>
        <p className={p}>
          {t("We and our service providers use cookies, pixels, tags, and similar technologies to operate the site, remember preferences, analyze traffic, and support advertising. Specifically, we use:")}
        </p>
        <ul className={ul}>
          <Bullet>{t("Google Tag Manager — to manage our tags;")}</Bullet>
          <Bullet>{t("Google Analytics 4 (GA4) — to measure site traffic and usage;")}</Bullet>
          <Bullet>
            {t("Google Ads — to measure advertising performance and conversions.")}
          </Bullet>
        </ul>
        <p className={p}>
          {t("These tools may set cookies and receive online identifiers (such as your IP address and cookie IDs) directly from your browser. You can control cookies through your browser settings. You can opt out of certain Google advertising at")}{" "}
          <a
            href="https://myadcenter.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            https://myadcenter.google.com
          </a>{" "}
          {t("and out of Google Analytics via the Google Analytics Opt-out Browser Add-on at")}{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            className={linkCls}
          >
            https://tools.google.com/dlpage/gaoptout
          </a>
          .
        </p>

        {/* 4 */}
        <h2 className={h2}>{t("4. How We Share Information")}</h2>
        <p className={p}>{t("We may disclose personal information to:")}</p>
        <ul className={ul}>
          <Bullet>
            {t("Service providers who perform services on our behalf (for example, hosting, payment processing, email delivery, analytics, and advertising);")}
          </Bullet>
          <Bullet>
            {t('Advertising and analytics partners (such as Google) for analytics and ad measurement — under California law, this disclosure for cross-context behavioral advertising may be considered "sharing";')}
          </Bullet>
          <Bullet>
            {t("Authorities or others for legal, safety, and compliance purposes, and in connection with a business transfer.")}
          </Bullet>
        </ul>
        <p className={p}>
          {t("We do not sell your personal information for monetary value.")}
        </p>
        <p className={p}>
          <span className="font-semibold text-slate-900">
            {t("Categories of third parties.")}
          </span>{" "}
          {t("We may disclose personal information to analytics and advertising providers (such as Google); payment processors; hosting, infrastructure, and email or communications providers; and professional advisors, regulators, or law enforcement where permitted or required by law.")}
        </p>
        <p className={p}>
          <span className="font-semibold text-slate-900">
            {t("Personal information shared for advertising.")}
          </span>{" "}
          {t('The categories of personal information that may be "shared" (as defined under California law) for cross-context behavioral advertising are identifiers (such as cookie IDs and IP address) and internet or other electronic network activity (such as pages viewed and interactions). We do not share sensitive personal information for this purpose, and we do not knowingly share the personal information of consumers under 16 years of age.')}
        </p>

        {/* 5 */}
        <h2 className={h2}>{t("5. Your California Privacy Rights")}</h2>
        <p className={p}>{t("If you are a California resident, you have the right to:")}</p>
        <ul className={ul}>
          <Bullet>
            {t("Know what personal information we collect, use, disclose, and share;")}
          </Bullet>
          <Bullet>{t("Access a copy of your personal information;")}</Bullet>
          <Bullet>{t("Request deletion of your personal information;")}</Bullet>
          <Bullet>{t("Correct inaccurate personal information;")}</Bullet>
          <Bullet>
            {t('Opt out of the "sale" or "sharing" of your personal information;')}
          </Bullet>
          <Bullet>{t("Limit the use of sensitive personal information;")}</Bullet>
          <Bullet>
            {t("Not be discriminated against for exercising your rights.")}
          </Bullet>
        </ul>

        {/* 6 */}
        <h2 id="do-not-sell" className={`${h2} scroll-mt-24`}>
          {t("6. Do Not Sell or Share My Personal Information")}
        </h2>
        <p className={p}>
          {t('We do not sell your personal information for monetary value. However, our use of analytics and advertising cookies (such as Google Analytics and Google Ads) may be considered "sharing" of personal information for cross-context behavioral advertising under California law. To opt out, you can:')}
        </p>
        <ul className={ul}>
          <Bullet>
            {t("Adjust your cookie and ad settings as described in Section 3;")}
          </Bullet>
          <Bullet>
            {t("Enable a Global Privacy Control (GPC) signal in your browser — we automatically detect and honor GPC as a request to opt out of the sale or sharing of your personal information; and/or")}
          </Bullet>
          <Bullet>
            {t("Contact us at")}{" "}
            <a href={`mailto:${EMAIL}`} className={linkCls}>
              {EMAIL}
            </a>{" "}
            {t('with the subject "Do Not Sell or Share."')}
          </Bullet>
        </ul>

        {/* 7 */}
        <h2 className={h2}>{t("7. Global Privacy Control (GPC)")}</h2>
        <p className={p}>
          {t("Some browsers and extensions let you broadcast a Global Privacy Control (GPC) signal. We automatically detect this signal and treat it as a request to opt out of the sale or sharing of your personal information for advertising and analytics — no account or sign-in is required. You can also limit these cookies through your browser settings and the Google opt-out tools described in Section 3, or email")}{" "}
          <a href={`mailto:${EMAIL}`} className={linkCls}>
            {EMAIL}
          </a>{" "}
          {t("to submit a request.")}
        </p>

        {/* 8 */}
        <h2 className={h2}>{t("8. How to Exercise Your Rights")}</h2>
        <p className={p}>
          {t("You, or an authorized agent acting on your behalf, may submit a request by emailing")}{" "}
          <a href={`mailto:${EMAIL}`} className={linkCls}>
            {EMAIL}
          </a>
          .{" "}
          {t("We will take steps to verify your request and will respond within the timeframes required by law. We will not discriminate against you for exercising your rights.")}
        </p>

        {/* 9 */}
        <h2 className={h2}>{t("9. Data Retention")}</h2>
        <p className={p}>
          {t("We retain each category of personal information for as long as necessary to fulfill the purposes described in this Policy. We determine the appropriate retention period based on the length of our relationship with you, the time needed to provide our services and support, our legal, tax, accounting, and regulatory obligations, and the need to resolve disputes and enforce our agreements. Analytics and advertising identifiers are retained according to the retention settings of the relevant provider (for example, Google Analytics). When personal information is no longer needed, we delete or de-identify it.")}
        </p>

        {/* 10 */}
        <h2 className={h2}>{t("10. Children's Privacy")}</h2>
        <p className={p}>
          {t("Our site is not directed to children under 16, and we do not knowingly collect personal information from them.")}
        </p>

        {/* 11 */}
        <h2 className={h2}>{t("11. Third-Party Links")}</h2>
        <p className={p}>
          {t("Our site may link to third-party websites. We are not responsible for their privacy practices.")}
        </p>

        {/* 12 */}
        <h2 className={h2}>{t("12. Changes to This Policy")}</h2>
        <p className={p}>
          {t('We may update this Policy from time to time. The "Last Updated" date above reflects the most recent revision.')}
        </p>

        {/* 13 */}
        <h2 className={h2}>{t("13. Contact Us")}</h2>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">SightLine Networks</p>
          <p className="mt-1 text-sm text-slate-600">
            {t("Email:")}{" "}
            <a href={`mailto:${EMAIL}`} className={linkCls}>
              {EMAIL}
            </a>
          </p>
          <div className="mt-5">
            <CtaButton
              variant="primary"
              ctaName="privacy-contact"
              page="privacy"
              onClick={() => setPage("contact")}
            >
              {t("Contact Us")} <ArrowRight className="h-4 w-4" />
            </CtaButton>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// MAIN APP COMPONENT
// =========================================================================

export default function SightlineWebsite() {
  // Every page is a real, crawlable URL in both languages — the initial state
  // is read from the address bar so direct hits, bookmarks, and Google's
  // crawler all land on the right page in the right language. See
  // src/i18n/routes.ts for the URL table and public/.htaccess for the SPA
  // fallback that lets the server hand these paths to index.html.
  //
  // resolveInitialLocation() additionally applies the first-visit
  // browser-language switch (bare "/" only, never for bots or the
  // prerenderer, never once a language has been chosen manually) — see
  // src/i18n/detect.ts for the full guard list.
  const [location, setLocation] = useState<{ page: PageKey; lang: Lang }>(() =>
    typeof window === "undefined"
      ? { page: "home", lang: "en" }
      : resolveInitialLocation(window.location.pathname)
  );
  const { page, lang } = location;
  const [mobileOpen, setMobileOpen] = useState(false);
  // Suppress the scroll-to-top effect for back/forward navigations, where the
  // browser restores the previous scroll position itself.
  const [isPopNavigation, setIsPopNavigation] = useState(false);

  /** Navigate within the current language, pushing a real URL. */
  const setPage = useCallback(
    (next: PageKey) => {
      setLocation((prev) => {
        if (typeof window !== "undefined") {
          window.history.pushState({}, "", pathFor(next, prev.lang));
        }
        return { page: next, lang: prev.lang };
      });
      setIsPopNavigation(false);
    },
    []
  );

  /** Switch language, staying on the equivalent page in the other tree. */
  const setLang = useCallback((next: Lang) => {
    // An explicit choice is permanent: it pins the language for future visits
    // and stops browser-language auto-detection from ever overriding it.
    writeLangPreference(next);
    setLocation((prev) => {
      if (prev.lang === next) return prev;
      if (typeof window !== "undefined") {
        window.history.pushState({}, "", pathFor(prev.page, next));
      }
      return { page: prev.page, lang: next };
    });
    setIsPopNavigation(false);
  }, []);

  // Back/forward buttons must move between pages, not exit the site.
  useEffect(() => {
    const onPopState = () => {
      setIsPopNavigation(true);
      setLocation(resolveLocation(window.location.pathname));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Scroll to top on page change (but not on back/forward — see above)
  useEffect(() => {
    if (!isPopNavigation) {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
    setMobileOpen(false);
    // isPopNavigation is intentionally excluded: this should run on navigation,
    // not when the flag itself flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, lang]);

  // On a direct load of e.g. /privacy#do-not-sell, scroll to the anchor once
  // the page has mounted (after the scroll-to-top effect above has run).
  useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => window.clearTimeout(t);
  }, []);

  const CurrentPage = useMemo(() => {
    switch (page) {
      case "home":
        return <HomePage setPage={setPage} />;
      case "services":
        return <ServicesPage setPage={setPage} />;
      case "hosting":
        return <HostingPage setPage={setPage} />;
      case "solutions":
        return <SolutionsPage setPage={setPage} />;
      case "areas":
        return <AreasPage setPage={setPage} />;
      case "about":
        return <AboutPage setPage={setPage} />;
      case "contact":
        return <ContactPage setPage={setPage} />;
      case "managed-it":
        return <ManagedItPage setPage={setPage} />;
      case "network-installation":
        return <NetworkInstallationPage setPage={setPage} />;
      case "security-cameras":
        return <SecurityCamerasPage setPage={setPage} />;
      case "server-cloud":
        return <ServerCloudPage setPage={setPage} />;
      case "website-development":
        return <WebsiteDevelopmentPage setPage={setPage} />;
      case "it-consulting":
        return <ItConsultingPage setPage={setPage} />;
      case "privacy":
        return <PrivacyPage setPage={setPage} />;
      default:
        return <HomePage setPage={setPage} />;
    }
    // Deliberately not keyed on `lang`: every page component reads the
    // translator from context, and React re-renders context consumers even when
    // a memoized parent element bails out. Re-keying here would just discard
    // the memo on every language switch for no benefit.
  }, [page, setPage]);

  return (
    <LanguageProvider lang={lang} setLang={setLang}>
      <div className="min-h-screen bg-white text-slate-900 antialiased">
        {/* SEO manager — updates title, canonical, hreflang, and schema */}
        <SeoManager page={page} lang={lang} />

        {/* Skip to main content (accessibility) */}
        <SkipLink />

        <Header
          setPage={setPage}
          page={page}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />

        <div id="main-content">{CurrentPage}</div>

        <Footer setPage={setPage} />

        {/* CCPA/CPRA notice-only privacy banner — global, dismissible, shown
            once per consent version. Rendered last so it overlays page content. */}
        <PrivacyNoticeBanner setPage={setPage} />
      </div>
    </LanguageProvider>
  );
}

/** Rendered inside the provider so its label can be translated. */
function SkipLink() {
  const t = useT();
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-sky-600 focus:px-4 focus:py-2 focus:text-white"
    >
      {t("Skip to main content")}
    </a>
  );
}
